// What to check when the test notification did not show, in the words and the
// menus of the device the person is holding.

import type { DevicePlatform } from 'modules/webPush'

export interface GuidanceStep {
    // A settings path. macOS / Windows / iOS menus are in English on almost
    // every device, so those are shown left-to-right, exactly as on screen.
    path?: string[]
    ltr?: boolean
    text: string
}

/** How the OS's notification settings list the app that shows our notifications. */
export const settingsAppName = (
    platform: DevicePlatform,
    browser: string
): string => {
    // An iPhone home-screen app has its own entry, under the manifest's name.
    if (platform === 'ios') return 'همیان'
    // Android lists Google's browser as plain «Chrome».
    if (platform === 'android' && browser === 'Google Chrome') return 'Chrome'
    return browser
}

/** The notification reached the browser and the device did not show it. */
export const deviceSteps = (
    platform: DevicePlatform,
    app: string
): GuidanceStep[] => {
    switch (platform) {
        case 'macos':
            return [
                {
                    path: ['System Settings', 'Notifications', app],
                    ltr: true,
                    text: '«Allow notifications» را روشن کنید و سبک هشدار را Banners یا Alerts بگذارید.',
                },
                {
                    text: 'اگر Focus یا Do Not Disturb روشن است (از Control Center بالای صفحه)، خاموشش کنید.',
                },
            ]
        case 'windows':
            return [
                {
                    path: ['Settings', 'System', 'Notifications'],
                    ltr: true,
                    text: `خود «Notifications» و در فهرست پایینش ${app} را روشن کنید.`,
                },
                { text: 'اگر «Do not disturb» روشن است، خاموشش کنید.' },
            ]
        case 'android':
            return [
                {
                    path: ['تنظیمات گوشی', 'برنامه‌ها', app, 'اعلان‌ها'],
                    text: 'روشن باشد، و دستهٔ اعلان‌های سایت‌ها هم خاموش نباشد.',
                },
                {
                    text: 'اگر همیان را روی صفحهٔ اصلی گوشی نصب کرده‌اید، برنامهٔ «همیان» را هم در همان فهرست بررسی کنید.',
                },
                { text: 'حالت «مزاحم نشوید» را خاموش کنید.' },
            ]
        case 'ios':
            return [
                {
                    // The home-screen app is listed under the manifest's name.
                    path: ['Settings', 'Notifications', app],
                    ltr: true,
                    text: '«Allow Notifications» را روشن کنید.',
                },
                { text: 'اگر Focus روشن است (از Control Center)، خاموشش کنید.' },
            ]
        default:
            return [
                {
                    text: `در تنظیمات اعلان سیستم‌عامل، اعلان‌های ${app} را روشن کنید.`,
                },
                { text: 'حالت «مزاحم نشوید» را خاموش کنید.' },
            ]
    }
}

/** The browser refused to show it at all: the site permission comes first. */
export const blockedSteps = (
    platform: DevicePlatform,
    app: string
): GuidanceStep[] => [
    {
        text: `از آیکون کنار نشانی سایت در ${app}، «اعلان‌ها» را روی «اجازه» بگذارید و صفحه را دوباره باز کنید.`,
    },
    ...deviceSteps(platform, app),
]

/** The push never reached this browser. */
export const deliverySteps = (
    platform: DevicePlatform,
    app: string
): GuidanceStep[] => {
    const mobile = platform === 'android' || platform === 'ios'
    return [
        { text: 'اینترنت این دستگاه را بررسی کنید.' },
        {
            text: mobile
                ? `${app} را از فهرست برنامه‌های باز ببندید و دوباره باز کنید.`
                : `${app} را کامل ببندید و دوباره باز کنید؛ اعلان فقط وقتی می‌رسد که مرورگر باز باشد.`,
        },
        ...(platform === 'android'
            ? [
                  {
                      path: ['تنظیمات گوشی', 'برنامه‌ها', app, 'باتری'],
                      text: '«بدون محدودیت» باشد؛ صرفه‌جویی باتری اعلان‌ها را نگه می‌دارد.',
                  },
              ]
            : []),
        {
            text: 'کلید «اعلان روی این دستگاه» را یک بار خاموش و دوباره روشن کنید.',
        },
        ...(mobile
            ? []
            : [
                  {
                      text: 'اگر باز هم نرسید، مرورگر دیگری مثل Chrome یا Edge را امتحان کنید.',
                  },
              ]),
    ]
}
